import React, { memo, useEffect, useState } from 'react'
import moment from 'moment-timezone'
import DeleteIcon from 'transfix-icons/build/DeleteIcon'
import { IconButton } from 'transfix-ui/components/IconButton'
import { ICustomerContact } from 'apps/fleet-planner/types'
import { IFacility, IFacilityAddress } from 'apps/fleet-planner/utils/api'
import { findPrimaryAddress, findPrimaryContact } from 'apps/fleet-planner/utils/facilities'
import { TableRow, TableCell } from 'transfix-ui'
import { isToday } from 'utils/appointmentHelpers'
import formatPhone from 'utils/formatPhone'
import FORMATS from 'constants/datetime'
import { DISPLAY } from 'constants/display'

interface IFacilitiesTableRow {
  facility: IFacility
  isUpdated?: boolean
  onEditRow: (facility: IFacility) => void
  onDeleteRow: (facility: IFacility) => void
}

const lastModifiedTime = (timeStamp: string) => {
  if (!timeStamp) return DISPLAY.empty
  const dateTime = moment(timeStamp).format(FORMATS.monthDayTimeFormatShort12h)
  const time = moment(timeStamp).format(FORMATS.timeFormatShort12H)
  return isToday(timeStamp) ? `Today, ${time}` : dateTime
}

export const FacilitiesTableRow = memo<IFacilitiesTableRow>(
  ({ facility, isUpdated = false, onDeleteRow, onEditRow }) => {
    const { addresses = [], contacts = [], name = '', id = '', updatedAt = '' } = facility || {}
    const [hasHighlight, setHasHighlight] = useState<boolean>(isUpdated)

    const parseFacilityAddress = (currentFacility?: IFacilityAddress): JSX.Element | string => {
      const { addressLine1 = '', addressLine2 = '' } = currentFacility || {}
      if (!addressLine1 && !addressLine2) return DISPLAY.empty
      return (
        <div>
          {addressLine1 && <p>{addressLine1}</p>}
          {addressLine2 && <p>{addressLine2}</p>}
        </div>
      )
    }

    const parseFacilityCity = (currentFacility?: IFacilityAddress): JSX.Element | string => {
      const { locality = '', subdivisionCode = '' } = currentFacility || {}
      if (!locality && !subdivisionCode) return DISPLAY.empty
      return (
        <span>
          {locality}
          {locality && subdivisionCode && ', '}
          {subdivisionCode}
        </span>
      )
    }

    const parsePrimaryContact = (currentContact?: ICustomerContact): JSX.Element | string => {
      const { email = '', name: customerName = '', phone = '' } = currentContact || {}
      if (!customerName && !phone && !email) return DISPLAY.empty
      return (
        <div>
          {customerName && <p>{customerName}</p>}
          {phone && <p className='fs-mask'>{formatPhone(phone)}</p>}
          {email && <p className='fs-mask'>{email}</p>}
        </div>
      )
    }

    const primaryAddress = findPrimaryAddress(addresses)
    const primaryContact = findPrimaryContact(contacts)

    /* Temporarily hiding the other contacts column until facility contact editing is implemented */
    // const parseOtherContacts = (): JSX.Element | string => {
    //   // there are cases where there are no primary contacts, therefore all contacts are considered 'other'
    //   const otherContacts = primaryContact ? contacts.length - 1 : contacts.length
    //   // TODO: [FP-1602] this should trigger a contact card upon hover
    //   if (otherContacts > 0) {
    //     return <span>{otherContacts} other contacts</span>
    //   }
    //   return DISPLAY.empty
    // }

    // if isUpdated is toggled, we want to highlight the row
    // but only do it once per mount
    useEffect(() => {
      if (isUpdated) {
        setHasHighlight(true)
        const timer = setTimeout(() => setHasHighlight(false), 3000)
        return () => clearTimeout(timer)
      }
      return undefined
    }, [])

    const rowClickHandler = () => {
      // TODO: [FP-1592] should show Edit Modal
      onEditRow(facility)
    }

    const rowDeleteHandler = (e: React.MouseEvent) => {
      e.stopPropagation() // prevent rowClickHandler from firing
      onDeleteRow(facility)
    }

    return (
      // TODO: [FP-1590] add search bar
      <TableRow
        dataTest='facility-row'
        className={`${hasHighlight ? 'fp-loads-list-row-updated' : ''} fp-loads-list-row`}
        onClick={rowClickHandler}>
        <TableCell className='pl6' dataTest={`list-row-facility-name-${id}`}>
          <strong>{name}</strong>
        </TableCell>
        <TableCell dataTest={`list-row-facility-address-${id}`}>
          {parseFacilityAddress(primaryAddress)}
        </TableCell>
        <TableCell dataTest={`list-row-facility-city-${id}`}>
          {parseFacilityCity(primaryAddress)}
        </TableCell>
        <TableCell dataTest={`list-row-facility-contact-${id}`}>
          {parsePrimaryContact(primaryContact)}
        </TableCell>
        {/* Temporarily hiding the other contacts column until facility contact editing is implemented */}
        {/* <TableCell dataTest={`list-row-facility-contacts-${id}`}>{parseOtherContacts()}</TableCell> */}
        <TableCell dataTest={`list-row-facility-last-modified-${id}`}>
          {lastModifiedTime(updatedAt)}
        </TableCell>
        <TableCell dataTest={`list-row-facility-delete-${id}`}>
          <IconButton
            Icon={DeleteIcon}
            color='tertiary'
            value={facility.id}
            onClick={rowDeleteHandler}
            data-test={`delete-facility-${facility.id}`}
            label='Delete'
            size='large'
          />
        </TableCell>
      </TableRow>
    )
  }
)
